import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, Users, Star, ArrowUpRight } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import StartupCard from "../../components/discover/StartupCard";
import { motion } from "framer-motion";

export default function TrendingStartupsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [activeFilters, setActiveFilters] = useState({
    industry: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/startups?limit=100")
      .then((r) => {
        if (alive) {
          // Filter only trending ones, or enrich with growth parameters if they are trending
          const rawItems = r.data.items || [];
          const trending = rawItems
            .map((c, index) => {
              const growthRates = ["+184% YoY", "+240% YoY", "+120% YoY", "+95% MoM", "+310% YoY"];
              const users = ["1.5M MAU", "2.8M MAU", "800K MAU", "4.2M MAU", "350K MAU"];
              const popularityStars = [5, 5, 4, 5, 4];
              return {
                ...c,
                growth: growthRates[index % growthRates.length],
                monthlyUsers: users[index % users.length],
                popularity: popularityStars[index % popularityStars.length],
              };
            });
          setItems(trending);
        }
      })
      .catch((e) => console.error("Error loading trending startups:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const getFilterOptions = (key) => {
    const vals = items.map((item) => item[key]).filter(Boolean);
    return Array.from(new Set(vals)).sort();
  };

  const industriesList = getFilterOptions("industry");
  const filters = [
    { key: "industry", label: "Industry", options: industriesList },
  ];

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.industry && item.industry.toLowerCase().includes(q));

    const matchesIndustry =
      activeFilters.industry === "All" || item.industry === activeFilters.industry;

    return matchesSearch && matchesIndustry;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Trending Startups"
      subtitle="Ecosystem leaders and rocketships displaying breakout month-on-month growth, user popularity, and funding velocity."
    >
      <div className="space-y-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search trending startups..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} trending startups
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Grid List */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No trending startups match your search.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedItems.map((c) => (
                <motion.div
                  key={c.slug}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-[24px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 group"
                >
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      {c.domain ? (
                        <img
                          src={`https://logo.clearbit.com/${c.domain}`}
                          alt={`${c.name} logo`}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                          className="w-12 h-12 rounded-2xl object-contain bg-white border border-gray-100 p-1 shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-[#7C3AED] font-display font-semibold flex items-center justify-center border border-indigo-100 shrink-0">
                          {c.name[0]}
                        </div>
                      )}

                      <div className="flex flex-col items-end gap-1.5">
                        <span className="text-[10px] font-bold px-2.5 py-1 bg-rose-50 text-rose-600 rounded-full border border-rose-100/50 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> {c.growth}
                        </span>
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                          {c.monthlyUsers}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-display font-bold text-gray-900 text-lg leading-snug group-hover:text-[#7C3AED] transition">
                        {c.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">
                        {c.description}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 mt-4">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-50 text-gray-500 rounded">
                        {c.industry}
                      </span>
                      {c.funding && c.funding !== "N/A" && (
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded">
                          💰 {c.funding}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${
                            i < c.popularity ? "text-amber-400 fill-amber-400" : "text-gray-200"
                          }`}
                        />
                      ))}
                    </div>

                    <a
                      href={`/discover/company/${c.slug}`}
                      className="text-xs font-bold text-[#7C3AED] hover:underline flex items-center gap-0.5"
                    >
                      View Analytics <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
